import { cn, Flex, Icon, type SvgIcon } from '@bridebook/aura';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';

export type SelectorButtonSize = 'lg' | 'standard' | 'sm';

const selectorButtonVariants = cva(
  [
    // Default
    'group flex min-w-[60px] cursor-pointer items-center justify-center overflow-hidden border border-space-15 bg-white px-3 typo-sm text-space transition-all duration-150 ease-out focus-visible:ring-[3px] focus-visible:ring-focus-ring focus-visible:ring-offset-2 focus-visible:outline-none',
    // Hover
    'hover:bg-indigo-crush-04',
    // Selected
    'data-[state=on]:bg-indigo-crush-04 [&[data-state=on]:not([data-disabled])]:border-indigo-crush [&[data-state=on]:not([data-disabled])]:text-indigo-crush',
    // Disabled
    'data-[disabled]:pointer-events-none data-[disabled]:cursor-not-allowed data-[disabled]:text-space/40 [&[data-state=on][data-disabled]]:border-indigo-crush/40 [&[data-state=on][data-disabled]]:text-indigo-crush/40',
  ],
  {
    variants: {
      size: {
        lg: 'h-[112px] flex-col gap-2 rounded-[20px]',
        standard: 'h-[60px] gap-1 rounded-[16px]',
        sm: 'h-[40px] gap-1 rounded-[12px]',
      },
    },
  },
);

const iconVariants = cva('', {
  variants: {
    size: {
      lg: 'size-[24px]',
      standard: 'size-[16px]',
      sm: 'size-[16px]',
    },
  },
});

type SelectorButtonBaseProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  asChild?: boolean;
  children?: ReactNode;
  icon?: SvgIcon;
};

type SelectorButtonDefaultProps = SelectorButtonBaseProps & {
  size?: 'standard' | 'sm';
  classNames?: {
    icon?: string;
    /**
     * Icon background only available for the large size
     */
    iconBackground: never;
  };
};

type SelectorButtonLargeProps = SelectorButtonBaseProps & {
  size?: 'lg';
  classNames?: {
    icon?: string;
    iconBackground?: string;
  };
};

export type SelectorButtonPresentationProps = SelectorButtonDefaultProps | SelectorButtonLargeProps;

/**
 * A presentation component for SelectorButton.
 * * @description
 * Primary use case: Designed to be wrapped by `ToggleGroup.Item`.
 * Secondary use case: Fully compatible with standalone `Toggle` primitives
 * or standard HTML buttons.
 * * @features
 * Relies on `data-state` and `data-disabled` attributes for styling.
 * Utilizes `Slottable` to preserve layout when used with `asChild`.
 */
export const SelectorButtonPresentation = forwardRef<
  HTMLButtonElement,
  SelectorButtonPresentationProps
>(
  (
    { children, asChild = false, icon, className, classNames, size = 'standard', ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    const hasIcon = !!icon;
    const isLargeSize = size === 'lg';

    const iconBackgroundClass = isLargeSize ? classNames?.iconBackground : undefined;

    return (
      <Comp className={cn(selectorButtonVariants({ size, className }))} ref={ref} {...props}>
        {hasIcon && (
          <>
            {isLargeSize ? (
              <Flex
                className={cn(
                  // Default
                  'h-10 w-10 items-center justify-center rounded-full bg-indigo-crush-10 text-space',
                  // Disabled
                  'group-data-[disabled]:opacity-40 group-[:not([data-state=on])]:group-data-[disabled]:bg-space-08',
                  // Disabled selected
                  'group-data-[state=on]:group-data-[disabled]:text-space/40 group-data-[state=on]:group-data-[disabled]:opacity-80',
                  iconBackgroundClass,
                )}>
                <Icon
                  icon={icon}
                  className={cn(iconVariants({ size, className: classNames?.icon }))}
                />
              </Flex>
            ) : (
              <Icon
                icon={icon}
                className={cn(iconVariants({ size, className: classNames?.icon }))}
              />
            )}
          </>
        )}
        <Slottable>{children}</Slottable>
      </Comp>
    );
  },
);
SelectorButtonPresentation.displayName = 'SelectorButtonPresentation';
